import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
  createRootRouteWithContext,
  Link,
  Outlet,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { QueryClient } from '@tanstack/react-query';
import { FathomLogo } from '@/components/ui/fathom-logo';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { initAnalytics, trackPageview } from '@/lib/analytics-client';
import { useTheme } from '@/lib/hooks/use-theme';
import '@/styles/globals.css';

const SITE_DEFAULT_DESCRIPTION =
  'Track your Fathom Coffee shipment from our Virginia Beach roastery to your door.';
const SITE_DEFAULT_TITLE = 'Track Your Shipment - Fathom Coffee';

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  if (typeof document === 'undefined') return;
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  if (typeof document === 'undefined') return;
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function applyHeadForPath(pathname: string) {
  if (typeof window === 'undefined') return;

  const isLookup = pathname.startsWith('/lookup');
  let title = SITE_DEFAULT_TITLE;
  let description = SITE_DEFAULT_DESCRIPTION;

  if (pathname.startsWith('/lookup/results')) {
    title = 'Your Order - Fathom Coffee';
    description = 'View your Fathom Coffee order and shipment status.';
  } else if (pathname.startsWith('/lookup')) {
    title = 'Find Your Order - Fathom Coffee';
    description =
      'Look up your Fathom Coffee order with your email and order number.';
  } else if (pathname.startsWith('/track/')) {
    title = 'Tracking - Fathom Coffee';
    description = 'Follow your Fathom Coffee shipment in real time.';
  }

  document.title = title;
  setMeta('description', description);
  setMeta('og:title', title, 'property');
  setMeta('og:description', description, 'property');
  setMeta('og:type', 'website', 'property');
  setMeta('og:site_name', 'Fathom Coffee', 'property');
  setMeta(
    'robots',
    isLookup ? 'noindex, nofollow' : 'noindex, nofollow'
  );

  const canonical = `${window.location.origin}${pathname}`;
  setLink('canonical', canonical);
  setMeta('og:url', canonical, 'property');
}

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

function RootLayout() {
  const { resolvedTheme, toggleTheme, mounted } = useTheme();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Initialize analytics once
  useEffect(() => {
    void initAnalytics();
  }, []);

  // Update document head (title, meta, canonical) on route changes
  useEffect(() => {
    applyHeadForPath(pathname);
  }, [pathname]);

  // Track page views on route changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    void trackPageview();

    const unsubscribe = router.subscribe('onResolved', () => {
      void trackPageview();
    });

    return () => unsubscribe();
  }, [router]);

  return (
    <>
      {/* Header */}
      <header className="relative overflow-hidden" style={{ backgroundColor: 'var(--header-bg)' }}>
        {/* Subtle texture overlay */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {/* Spacer for alignment */}
            <div className="w-10" />

            {/* Center logo */}
            <Link to="/" className="inline-block group">
              <div className="flex justify-center mb-2">
                <FathomLogo className="h-16 md:h-20 w-auto text-header-text transition-all group-hover:text-fathom-gold" />
              </div>
              <p className="font-accent text-fathom-gold text-base text-center">
                A deeper love for coffee.
              </p>
            </Link>

            {/* Theme toggle */}
            <ThemeToggle
              theme={mounted ? resolvedTheme : 'light'}
              onToggle={toggleTheme}
              className="text-fathom-gold hover:bg-white/10"
            />
          </div>
        </div>

        {/* Gold accent bar */}
        <div className="h-1 bg-gradient-to-r from-fathom-gold/60 via-fathom-gold to-fathom-gold/60" />
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 md:py-12">
        {/*
          Outer Sentry.ErrorBoundary reports render errors to Sentry; the inner
          application ErrorBoundary renders the branded fallback UI.
        */}
        <Sentry.ErrorBoundary fallback={<div role="alert" /> /* fall through to inner boundary */}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </Sentry.ErrorBoundary>
      </main>

      {/* Footer */}
      <footer className="mt-auto" style={{ backgroundColor: 'var(--footer-bg)' }}>
        <div className="h-px bg-gradient-to-r from-transparent via-fathom-gold/40 to-transparent" />

        <div className="container mx-auto px-4 py-10">
          <div className="text-center space-y-4">
            <a href="https://fathomcoffee.com" className="inline-block group">
              <p className="font-display text-xl tracking-[0.15em] text-fathom-gold transition-all group-hover:tracking-[0.2em]">
                FATHOM COFFEE
              </p>
            </a>

            <nav className="flex items-center justify-center gap-6 text-sm">
              <a
                href="https://fathomcoffee.com/shop"
                className="transition-colors hover:text-fathom-gold"
                style={{ color: 'var(--header-text)', opacity: 0.7 }}
              >
                Shop
              </a>
              <span className="w-1 h-1 rounded-full bg-fathom-gold/40" />
              <a
                href="https://fathomcoffee.com/story"
                className="transition-colors hover:text-fathom-gold"
                style={{ color: 'var(--header-text)', opacity: 0.7 }}
              >
                Our Story
              </a>
              <span className="w-1 h-1 rounded-full bg-fathom-gold/40" />
              <a
                href="https://fathomcoffee.com/contact-us"
                className="transition-colors hover:text-fathom-gold"
                style={{ color: 'var(--header-text)', opacity: 0.7 }}
              >
                Contact
              </a>
            </nav>

            <div className="pt-4">
              <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--header-text)', opacity: 0.3 }}>
                Roasted by the ocean in Virginia Beach, VA
              </p>
            </div>

            <p className="text-xs pt-2" style={{ color: 'var(--header-text)', opacity: 0.4 }}>
              © {new Date().getFullYear()} Fathom Coffee Co.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
